import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { fromEvent } from 'rxjs';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { TIM_ASSISTANT_HOVER_LABEL } from '../../constants/ai-help-assistant-home-static';
import { AiHelpAssistantStateService } from '../../services/ai-help-assistant-state.service';

interface FabPosition {
  left: number;
  top: number;
}

const FAB_POSITION_STORAGE_KEY = 'ai-help-fab-position';
const FAB_SHELL_VISIBLE_STORAGE_KEY = 'ai-help-fab-shell-visible';
const DRAG_THRESHOLD_PX = 8;
/** Matches SCSS: label + GIF hide below 1300px, leaving the Tim image only. */
const HEADER_IMAGE_ONLY_MQ = '(max-width: 1299px)';

@Component({
  selector: 'app-ai-help-assistant-fab',
  standalone: true,
  imports: [MatIconModule, ...matTooltipSmartImports],
  templateUrl: './ai-help-assistant-fab.html',
  styleUrl: './ai-help-assistant-fab.scss'
})
export class AiHelpAssistantFab implements OnInit {
  /** `header` docks the trigger in the app header; `floating` keeps the draggable corner FAB. */
  @Input() placement: 'floating' | 'header' = 'floating';

  readonly state = inject(AiHelpAssistantStateService);
  readonly timHoverLabel = TIM_ASSISTANT_HOVER_LABEL;
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  fabPosition: FabPosition | null = null;
  isDragging = false;
  readonly isShellVisible = signal(true);
  readonly isHeaderImageOnly = signal(false);

  private dragMoved = false;
  private startPointerX = 0;
  private startPointerY = 0;
  private originLeft = 0;
  private originTop = 0;
  private activePointerId: number | null = null;

  @HostBinding('class.ai-help-fab-host--custom')
  get hasCustomPosition(): boolean {
    return this.isHeaderPlacement() ? false : this.isShellVisible() && this.fabPosition !== null;
  }

  @HostBinding('class.ai-help-fab-host--header')
  get headerPlacementClass(): boolean {
    return this.isHeaderPlacement();
  }

  @HostBinding('class.ai-help-fab-host--dragging')
  get hostDragging(): boolean {
    return !this.isHeaderPlacement() && this.isDragging;
  }

  @HostBinding('class.ai-help-fab-host--minimized')
  get hostMinimized(): boolean {
    return !this.isHeaderPlacement() && !this.isShellVisible();
  }

  @HostBinding('style.left.px')
  get hostLeft(): number | null {
    if (this.isHeaderPlacement() || !this.isShellVisible()) {
      return null;
    }
    return this.fabPosition?.left ?? null;
  }

  @HostBinding('style.top.px')
  get hostTop(): number | null {
    if (this.isHeaderPlacement() || !this.isShellVisible()) {
      return null;
    }
    return this.fabPosition?.top ?? null;
  }

  @HostBinding('style.right')
  get hostRight(): string | null {
    if (this.isHeaderPlacement() || !this.isShellVisible()) {
      return null;
    }
    return this.fabPosition ? 'auto' : null;
  }

  @HostBinding('style.bottom')
  get hostBottom(): string | null {
    if (this.isHeaderPlacement() || !this.isShellVisible()) {
      return null;
    }
    return this.fabPosition ? 'auto' : null;
  }

  isHeaderPlacement(): boolean {
    return this.placement === 'header';
  }

  ngOnInit(): void {
    if (this.isHeaderPlacement()) {
      this.isShellVisible.set(true);
      this.bindHeaderImageOnlyMedia();
      return;
    }

    this.fabPosition = this.loadSavedPosition();
    this.isShellVisible.set(this.loadShellVisible());

    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clampSavedPosition());
  }

  onHeaderFabClick(event: Event): void {
    if (!this.isHeaderPlacement() || this.state.isOpen()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.state.toggle();
  }

  minimizeShell(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.resetDragState();
    this.isShellVisible.set(false);
    this.saveShellVisible(false);
  }

  restoreShell(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.resetDragState();
    this.isShellVisible.set(true);
    this.saveShellVisible(true);
  }

  onDragStart(event: PointerEvent): void {
    if (this.isHeaderPlacement()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('.ai-help-fab-shell__minimize')) {
      return;
    }
    this.beginDrag(event);
  }

  @HostListener('document:pointermove', ['$event'])
  onDragMove(event: PointerEvent): void {
    if (!this.isDragging || event.pointerId !== this.activePointerId) {
      return;
    }

    const deltaX = event.clientX - this.startPointerX;
    const deltaY = event.clientY - this.startPointerY;

    if (!this.dragMoved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) {
      return;
    }

    this.dragMoved = true;
    event.preventDefault();

    this.fabPosition = this.clampPosition(this.originLeft + deltaX, this.originTop + deltaY);
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onDragEnd(event: PointerEvent): void {
    if (!this.isDragging || event.pointerId !== this.activePointerId) {
      return;
    }

    const didDrag = this.dragMoved;
    this.resetDragState();

    if (didDrag) {
      if (this.fabPosition) {
        this.savePosition(this.fabPosition);
      }
      return;
    }

    // Pure click on the Tim FAB: open/close the assistant panel.
    this.state.toggle();
  }

  private bindHeaderImageOnlyMedia(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mq = window.matchMedia(HEADER_IMAGE_ONLY_MQ);
    this.isHeaderImageOnly.set(mq.matches);

    const onChange = (event: MediaQueryListEvent) => this.isHeaderImageOnly.set(event.matches);
    mq.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => mq.removeEventListener('change', onChange));
  }

  private beginDrag(event: PointerEvent): void {
    if (
      this.isHeaderPlacement() ||
      event.button !== 0 ||
      this.state.isOpen() ||
      !this.isShellVisible()
    ) {
      return;
    }

    const rect = this.host.nativeElement.getBoundingClientRect();
    this.startPointerX = event.clientX;
    this.startPointerY = event.clientY;
    this.originLeft = rect.left;
    this.originTop = rect.top;
    this.dragMoved = false;
    this.isDragging = true;
    this.activePointerId = event.pointerId;
  }

  private resetDragState(): void {
    this.isDragging = false;
    this.activePointerId = null;
    this.dragMoved = false;
  }

  private loadSavedPosition(): FabPosition | null {
    try {
      const raw = localStorage.getItem(FAB_POSITION_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Partial<FabPosition>;
      if (typeof parsed.left !== 'number' || typeof parsed.top !== 'number') {
        return null;
      }

      return this.clampPosition(parsed.left, parsed.top);
    } catch {
      return null;
    }
  }

  private savePosition(position: FabPosition): void {
    try {
      localStorage.setItem(FAB_POSITION_STORAGE_KEY, JSON.stringify(position));
    } catch {
      // Ignore quota / private-mode failures.
    }
  }

  private loadShellVisible(): boolean {
    try {
      const raw = localStorage.getItem(FAB_SHELL_VISIBLE_STORAGE_KEY);
      if (raw === null) {
        return true;
      }
      return raw !== 'false';
    } catch {
      return true;
    }
  }

  private saveShellVisible(visible: boolean): void {
    try {
      localStorage.setItem(FAB_SHELL_VISIBLE_STORAGE_KEY, String(visible));
    } catch {
      // Ignore quota / private-mode failures.
    }
  }

  private clampSavedPosition(): void {
    if (!this.fabPosition || !this.isShellVisible()) {
      return;
    }

    this.fabPosition = this.clampPosition(this.fabPosition.left, this.fabPosition.top);
  }

  private clampPosition(left: number, top: number): FabPosition {
    const el = this.host.nativeElement;
    const width = el.offsetWidth || 135;
    const height = el.offsetHeight || 135;
    const maxLeft = Math.max(0, window.innerWidth - width);
    const maxTop = Math.max(0, window.innerHeight - height);

    return {
      left: Math.min(Math.max(0, left), maxLeft),
      top: Math.min(Math.max(0, top), maxTop)
    };
  }
}
